import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { createLLMOMeta, createBreadcrumbSchema, createToolSchema, createHowToSchema, createFAQSchema } from "@/lib/seo"
import { getToolBySlug } from "@/data/tools/tools-registry"
import ToolPageLayout from "@/components/tools/ToolPageLayout"
import ToolResultCard from "@/components/tools/ToolResultCard"
import { NumberField, SelectField, SubmitButton, ResetButton } from "@/components/tools/ToolFormField"
import { SectionHeading, Paragraph, NumberedSteps, FeatureGrid, StatGrid, UseCaseCards, ComparisonTable, TipList, ContentSection } from "@/components/tools/SeoContent"

const tool = getToolBySlug("instagram-dm-roi-calculator")!

const RESPONSE_SPEEDS = [
  { value: "fast", label: "Fast (1-2 hours)", multiplier: 1.5 },
  { value: "medium", label: "Medium (2-6 hours)", multiplier: 2.5 },
  { value: "slow", label: "Slow (6-24 hours)", multiplier: 4.0 },
] as const

export const Route = createFileRoute("/tools/instagram-dm-roi-calculator")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: tool.seo.title,
        description: tool.seo.description,
        url: "https://zapify.pro/tools/instagram-dm-roi-calculator",
      }),
      { name: "keywords", content: tool.seo.keywords.join(", ") },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/tools/instagram-dm-roi-calculator" }],
    scripts: [
      createToolSchema({ name: tool.name, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-dm-roi-calculator" }),
      createBreadcrumbSchema({ itemListElement: [
        { position: 1, name: "Home", item: "https://zapify.pro" },
        { position: 2, name: "Free Tools", item: "https://zapify.pro/tools" },
        { position: 3, name: tool.shortName, item: "https://zapify.pro/tools/instagram-dm-roi-calculator" },
      ]}),
      createHowToSchema({ name: "How to Calculate DM Automation ROI", description: tool.seo.description, steps: tool.howToSteps }),
      createFAQSchema(tool.faq),
    ],
  }),
  component: DMROICalculatorPage,
})

type Verdict = "worth-it" | "likely" | "maybe" | "not-yet"

function DMROICalculatorPage() {
  const [weeklyDMs, setWeeklyDMs] = useState("")
  const [minutesPerDM, setMinutesPerDM] = useState("3")
  const [hourlyRate, setHourlyRate] = useState("")
  const [saleValue, setSaleValue] = useState("")
  const [responseSpeed, setResponseSpeed] = useState<"fast" | "medium" | "slow">("medium")
  const [toolCost, setToolCost] = useState("15")
  const [result, setResult] = useState<{
    netROI: number
    annualHoursSaved: number
    annualTimeSavings: number
    conversionLift: number
    annualRevenueGain: number
    paybackDays: number
    verdict: Verdict
    verdictLabel: string
  } | null>(null)

  const calculate = () => {
    const dms = Number(weeklyDMs) || 0
    const mins = Number(minutesPerDM) || 3
    const rate = Number(hourlyRate) || 0
    const sale = Number(saleValue) || 0
    const cost = Number(toolCost) || 15
    if (dms <= 0) return

    const speedData = RESPONSE_SPEEDS.find((s) => s.value === responseSpeed)!

    const weeklyMinutes = dms * mins
    const annualHours = (weeklyMinutes * 52) / 60
    const annualTimeSavings = annualHours * rate

    const baselineConversion = 0.05
    const currentMonthlyRevenue = dms * baselineConversion * sale * 4
    const projectedMonthlyRevenue = currentMonthlyRevenue * speedData.multiplier
    const monthlyRevenueGain = projectedMonthlyRevenue - currentMonthlyRevenue
    const annualRevenueGain = monthlyRevenueGain * 12

    const annualToolCost = cost * 12
    const totalBenefit = annualTimeSavings + annualRevenueGain
    const netROI = annualToolCost > 0 ? ((totalBenefit - annualToolCost) / annualToolCost) * 100 : 0
    const monthlyBenefit = totalBenefit / 12
    const paybackDays = monthlyBenefit > 0 ? (cost / monthlyBenefit) * 30 : 999

    let verdict: Verdict
    let verdictLabel: string
    if (netROI > 200) { verdict = "worth-it"; verdictLabel = "Absolutely Worth It" }
    else if (netROI > 50) { verdict = "likely"; verdictLabel = "Likely Worth It" }
    else if (netROI > 0) { verdict = "maybe"; verdictLabel = "Maybe — Depends on Your Goals" }
    else { verdict = "not-yet"; verdictLabel = "Not Yet — Build Volume First" }

    setResult({
      netROI, annualHoursSaved: annualHours, annualTimeSavings,
      conversionLift: speedData.multiplier, annualRevenueGain, paybackDays, verdict, verdictLabel,
    })
  }

  const verdictColors: Record<Verdict, string> = {
    "worth-it": "bg-green-50 border-green-300 text-green-800 dark:bg-green-900/20 dark:border-green-700 dark:text-green-400",
    "likely": "bg-blue-50 border-blue-300 text-blue-800 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-400",
    "maybe": "bg-yellow-50 border-yellow-300 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-700 dark:text-yellow-400",
    "not-yet": "bg-red-50 border-red-300 text-red-800 dark:bg-red-900/20 dark:border-red-700 dark:text-red-400",
  }

  const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`

  return (
    <ToolPageLayout
      tool={tool}
      content={
        <div className="space-y-10">
          {/* Section 1: What is Instagram DM ROI? */}
          <ContentSection>
            <SectionHeading>What is Instagram DM ROI?</SectionHeading>
            <div className="space-y-4">
              <Paragraph>
                Instagram DM ROI (Return on Investment) measures the financial return you get from automating your direct message workflows compared to the cost of the automation tool. It accounts for two core value streams: the <strong className="text-neutral-800 dark:text-neutral-200">time you save</strong> by not manually typing every reply, and the <strong className="text-neutral-800 dark:text-neutral-200">additional revenue you earn</strong> from faster, more consistent responses that convert followers into customers.
              </Paragraph>
              <Paragraph>
                The numbers make a compelling case. Instagram DMs have a roughly <strong className="text-neutral-800 dark:text-neutral-200">90% open rate</strong>, dwarfing the 20-30% open rates of email marketing. When a follower sends you a DM or comments on a post expecting a reply, they are in a high-intent moment. Respond instantly, and studies show you can achieve a <strong className="text-neutral-800 dark:text-neutral-200">3-5x conversion lift</strong> compared to responding hours later. Every minute of delay costs you money. A Harvard Business Review study found that leads contacted within 5 minutes are 21 times more likely to convert than those contacted after 30 minutes.
              </Paragraph>
              <Paragraph>
                For creators, coaches, and small businesses on Instagram, the question is no longer <em>whether</em> DM automation works. The research is clear: instant, personalized responses dramatically outperform slow manual ones. The real question is whether automation is worth it <strong className="text-neutral-800 dark:text-neutral-200">for your specific numbers</strong> -- your DM volume, your average sale value, and how you currently spend your time. That is exactly what this calculator helps you figure out. Plug in your real data and get a verdict based on math, not hype.
              </Paragraph>
            </div>
          </ContentSection>

          {/* Section 2: The Real Numbers Behind DM Automation */}
          <ContentSection>
            <SectionHeading>The Real Numbers Behind DM Automation</SectionHeading>
            <StatGrid stats={[
              { value: "90%", label: "DM Open Rate", sublabel: "vs 20-30% email" },
              { value: "3-5x", label: "Conversion Lift", sublabel: "from instant responses" },
              { value: "250h", label: "Hours Saved/Year", sublabel: "at 50 DMs/day" },
              { value: "420%", label: "Average ROI", sublabel: "from Instagram marketing" },
            ]} />
            <Paragraph className="mt-4 text-sm">
              These are not theoretical figures. They are drawn from aggregated data across Instagram marketing platforms and creator surveys. The open rate gap alone means that a message sent via DM is roughly 3-4 times more likely to be <em>seen</em> than the same message sent via email -- before you even factor in response quality and speed.
            </Paragraph>
          </ContentSection>

          {/* Section 3: How to Calculate DM Automation ROI */}
          <ContentSection>
            <SectionHeading>How to Calculate DM Automation ROI</SectionHeading>
            <Paragraph>
              Calculating your DM automation ROI does not require a finance degree. Follow these four steps to get a clear picture of whether automation makes financial sense for your Instagram business.
            </Paragraph>
            <NumberedSteps steps={[
              {
                title: "Count Your Weekly DMs",
                description: "Open your Instagram inbox and count how many DM conversations you handle in a typical week. Include comment-triggered DMs, story replies, and inbound messages from potential customers. If you run promotions or use \"comment a keyword to get the link\" strategies, count those too -- they are often the highest-volume, highest-intent messages. Most creators underestimate their DM volume by 30-50% because many messages go unanswered and fall out of sight. Check your message requests folder as well. If you are getting 20+ meaningful DMs per week, automation starts to deliver serious value.",
              },
              {
                title: "Measure Your Current Response Time",
                description: "How long does it take you to reply to the average DM right now? Be honest. If you batch your replies twice a day, your average response time is likely 4-8 hours. If you check sporadically between tasks, it could be 6-24 hours. Response time is the single biggest lever in the ROI equation because it directly correlates with conversion rates. Instant responses (under 5 minutes) convert at 3-5x the rate of responses that take several hours. A follower who messages you about a product and gets a reply 6 hours later has likely already lost interest, found an alternative, or forgotten why they messaged in the first place.",
              },
              {
                title: "Calculate Your Time Value",
                description: "Estimate how much each minute spent on DMs costs you. If you freelance or consult at $75/hour, every 3-minute DM reply costs you $3.75 in opportunity cost. Multiply that across 50 DMs per week and you are spending $187.50 per week -- over $9,700 per year -- just on DM responses. Even if you value your time at a modest $30/hour, that is still $3,900 per year in time spent typing replies that could be automated. This calculator factors in your specific hourly rate to give you a precise dollar figure for the time you would reclaim.",
              },
              {
                title: "Factor in the Conversion Lift",
                description: "Time savings alone often justify the cost of automation. But the real ROI multiplier is the revenue you gain from faster responses. When you respond instantly to a follower asking about your course, coaching program, or product, you catch them at peak interest. Our calculator models the conversion lift based on your current response speed: switching from slow (6-24 hour) manual replies to instant automated replies can yield a 4x increase in conversion rate. If you currently make $2,000/month from DM-driven sales with slow responses, that same volume could generate $8,000/month with instant automation. The difference -- $6,000/month or $72,000/year -- is the revenue gain that makes DM automation one of the highest-ROI investments a creator can make.",
              },
            ]} />
          </ContentSection>

          {/* Section 4: When DM Automation IS Worth It */}
          <ContentSection>
            <SectionHeading>When DM Automation IS Worth It</SectionHeading>
            <Paragraph>
              Not every creator needs DM automation right now -- but most will eventually. Here are the three scenarios where automation delivers an undeniable return.
            </Paragraph>
            <UseCaseCards cases={[
              {
                badge: "HIGH VOLUME",
                badgeColor: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
                title: "High Volume: 20+ Weekly DMs",
                description: "If you receive 20 or more DMs per week, you are already spending 1-3 hours weekly just on replies. That is 50-150 hours per year. At any reasonable hourly rate, the time savings alone exceed the cost of an automation tool many times over. High-volume creators -- especially those who use \"comment to get the link\" CTAs, run giveaways, or post product content regularly -- often see 100-500+ DMs per week. At that scale, manual responses are not just inefficient; they are impossible. Messages get missed, followers get frustrated, and sales slip through the cracks. Automation ensures every single message gets an instant, on-brand response. For high-volume accounts, ROI is typically 500-2,000%+.",
              },
              {
                badge: "REVENUE FOCUS",
                badgeColor: "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
                title: "Revenue Focus: Affiliate Links or Product Sales",
                description: "If you monetize through affiliate links, digital products, courses, coaching, or e-commerce, every DM is a potential sale. The conversion lift from instant responses is where the big money is. Consider a creator selling a $97 digital course. With manual responses averaging 4 hours, they convert about 5% of DM inquiries -- that is 5 sales out of 100 DMs, or $485. With instant automated responses, that conversion rate jumps to 15-20%, yielding $1,455-$1,940 from the same 100 DMs. The $15-50/month automation cost is negligible compared to the $1,000+ in additional monthly revenue. If you have a clear offer and a way to take payment, DM automation is almost certainly worth it.",
              },
              {
                badge: "TIME SCARCITY",
                badgeColor: "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400",
                title: "Time Scarcity: Your Time is Worth $30+/Hour",
                description: "If your time is worth $30/hour or more -- whether through freelancing, consulting, content creation, or a day job -- the math tips decisively in favor of automation. Spending 3 minutes per DM at $30/hour means each reply costs you $1.50 in opportunity cost. Handle 30 DMs per week and that is $45/week or $2,340/year. An automation tool at $15-50/month ($180-600/year) saves you $1,740-2,160 annually in time value alone -- before counting any conversion gains. For creators and entrepreneurs whose time is worth $75-200/hour, the ROI becomes staggering: 1,000%+ returns are typical. Every minute you spend typing a DM reply is a minute you are not creating content, closing deals, or building your brand.",
              },
            ]} />
          </ContentSection>

          {/* Section 5: When to Skip Automation (For Now) */}
          <ContentSection>
            <SectionHeading>When to Skip Automation (For Now)</SectionHeading>
            <Paragraph>
              Automation is not a magic bullet. Here are three situations where it may not be the right investment yet -- though you should revisit as your account grows.
            </Paragraph>
            <TipList tips={[
              {
                title: "Low Volume (under 10 weekly DMs)",
                description: "If you are getting fewer than 10 DMs per week, the time savings are minimal (under 30 minutes/week). Your money is better spent on growing your audience first. Focus on content creation, engagement strategies, and building the kind of following that generates DM volume. Once you cross the 20 DM/week threshold, revisit this calculator.",
              },
              {
                title: "Complex Sales Requiring Custom Consultations",
                description: "If every sale requires a 30-minute discovery call, custom proposal, or deep personalization that AI cannot replicate, automation handles the top of the funnel but will not close deals for you. That said, automation can still qualify leads and schedule calls automatically -- so even complex-sale businesses often benefit from partial automation. The ROI just takes longer to materialize.",
              },
              {
                title: "No Clear Offer (Not Monetizing Yet)",
                description: "If you are not selling anything -- no product, no service, no affiliate links -- then the \"revenue gain\" side of the ROI equation is zero. Automation still saves time, but the payback period is longer when the only benefit is time savings. Build your offer first, then automate the delivery and follow-up. The calculator will show you exactly when the numbers tip in your favor.",
              },
            ]} />
          </ContentSection>

          {/* Section 6: Method Comparison Table */}
          <ContentSection>
            <SectionHeading>DM Response Method Comparison</SectionHeading>
            <Paragraph>
              There are three main ways to handle Instagram DMs: doing it yourself, hiring a virtual assistant, or using automation software. Here is how they compare across every dimension that matters.
            </Paragraph>
            <ComparisonTable
              headers={["Method", "Monthly Cost", "Response Time", "Best For"]}
              rows={[
                { method: "Manual (DIY)", values: ["$0", "2-6 hours", "Low volume (<10 DMs/week)"] },
                { method: "Virtual Assistant", values: ["$300-$800/mo", "30 min - 2 hours", "Complex, personalized replies"] },
                { method: "DM Automation", values: ["$15-$50/mo", "Instant", "High volume, revenue-focused"], highlight: true },
              ]}
            />
            <Paragraph className="mt-4 text-sm">
              Manual responses are free in dollars but expensive in time. A virtual assistant reduces your personal time investment but adds significant cost ($3,600-9,600/year) and still cannot match the speed of automation. DM automation delivers the best of both worlds: near-zero time investment, the lowest cost, and the fastest response time. For most creators, automation replaces 90%+ of manual DM work while outperforming a VA on speed and consistency.
            </Paragraph>
          </ContentSection>

          {/* Section 7: How Zapify DM Automation Works */}
          <ContentSection>
            <SectionHeading>How Zapify DM Automation Works</SectionHeading>
            <Paragraph>
              Zapify is built specifically for Instagram creators and businesses who want to turn DMs into a revenue channel without spending hours in their inbox. Here is what makes it different.
            </Paragraph>
            <FeatureGrid columns={2} items={[
              {
                icon: "\u26A1",
                title: "Instant Responses",
                description: "When a follower comments a keyword on your post or sends you a DM, Zapify responds in seconds with the message you configured. No delays, no missed leads, no manual effort. Set up automations for specific posts, your next post, or all posts at once.",
              },
              {
                icon: "\uD83D\uDEE1\uFE0F",
                title: "Official Meta API",
                description: "Zapify uses the official Instagram Graph API approved by Meta. Unlike browser-extension tools or unofficial bots that can get your account restricted or banned, Zapify operates within Instagram's terms of service. Your account is completely safe.",
              },
              {
                icon: "\u23F1\uFE0F",
                title: "5-Minute Setup",
                description: "Connect your Instagram business account, pick a post (or set a global trigger), write your automated reply, and you are live. No coding, no complicated workflows, no technical skills required. Most creators have their first automation running in under 5 minutes.",
              },
              {
                icon: "\uD83D\uDCCA",
                title: "Built-in Analytics",
                description: "See exactly how many DMs were sent, how many links were clicked, and how your automations perform over time. Zapify's dashboard gives you the data you need to optimize your messaging and maximize conversions. No guesswork -- just clear numbers on what is working.",
              },
            ]} />
            <Paragraph className="mt-5">
              Whether you are a creator selling digital products, a coach booking discovery calls, or an e-commerce brand driving sales from Instagram, Zapify turns your DMs from a time drain into your most profitable channel. The free tier includes 500 DMs/month so you can test the ROI before committing to a paid plan.
            </Paragraph>
          </ContentSection>
        </div>
      }
    >
      <div className="max-w-2xl mx-auto">
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
          {!result ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <NumberField label="Weekly DMs" value={weeklyDMs} onChange={setWeeklyDMs} placeholder="e.g. 50" required min={1} />
                <NumberField label="Minutes per DM" value={minutesPerDM} onChange={setMinutesPerDM} min={1} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <NumberField label="Your Hourly Rate ($)" value={hourlyRate} onChange={setHourlyRate} placeholder="e.g. 50" min={0} />
                <NumberField label="Avg Sale Value ($)" value={saleValue} onChange={setSaleValue} placeholder="e.g. 100" min={0} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <SelectField
                  label="Current Response Speed"
                  value={responseSpeed}
                  onChange={(v) => setResponseSpeed(v as "fast" | "medium" | "slow")}
                  options={RESPONSE_SPEEDS.map(s => ({ value: s.value, label: s.label }))}
                />
                <NumberField label="Tool Cost ($/mo)" value={toolCost} onChange={setToolCost} min={0} />
              </div>
              <SubmitButton onClick={calculate} disabled={!weeklyDMs}>Calculate ROI</SubmitButton>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Verdict banner */}
              <div className={`rounded-xl border p-5 text-center ${verdictColors[result.verdict]}`}>
                <p className="text-sm font-semibold uppercase tracking-wider mb-1">Verdict</p>
                <p className="text-2xl sm:text-3xl font-bold">{result.netROI > 0 ? "+" : ""}{Math.round(result.netROI)}% ROI</p>
                <p className="text-sm font-medium mt-1">{result.verdictLabel}</p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <ToolResultCard label="Hours Saved/Year" value={`${result.annualHoursSaved.toFixed(0)}h`} />
                <ToolResultCard label="Time Value Saved" value={fmt(result.annualTimeSavings)} variant="primary" />
                <ToolResultCard label="Conversion Lift" value={`${result.conversionLift}x`} />
                <ToolResultCard label="Added Revenue/Year" value={fmt(result.annualRevenueGain)} variant="success" />
                <ToolResultCard label="Payback Period" value={result.paybackDays < 365 ? `${Math.round(result.paybackDays)} days` : "365+ days"} />
              </div>

              <ResetButton onClick={() => setResult(null)}>Calculate Again</ResetButton>
            </div>
          )}
        </div>
      </div>
    </ToolPageLayout>
  )
}
